import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
/* eslint-disable max-lines */
const DEFAULT_ERROR_MESSAGE =
  'Request failed. Please try again. If the problem persists, contact support.';

const ERROR_TRANSLATIONS = {
  'errors.notAuthorized':
    'You are not authorized to perform this action. Please contact support if you believe this is an error.',
  'errors.editNotAllowed':
    'This action is not allowed. Please contact support if you need assistance.',
  'errors.unKnownError':
    'An unexpected error occurred. Please try again or contact support if the problem persists.',
  'errors.internalServerError':
    'An unexpected issue occurred while processing your request. Please try again in a few minutes. If the problem continues, contact support with details about the action you were performing.',
  'errors.validationError':
    'Some of the selected options are not supported due to a system configuration issue. If possible, try adjusting your selections. If no alternative selections are available, please contact support for assistance.',
  // POM-6242: only reached when the backend message is missing; the message itself names the
  // drug and state and is preferred by the code list in getErrorMessage.
  'errors.orderRejectedByAcceptanceRule':
    'This order cannot be accepted for the requested shipping state. Check the shipping address and the items, then try again.',
  'errors.inputValidationError':
    'The provided input is invalid. Please check your data and try again.',
  'errors.invalidInput': 'Invalid input provided. Please verify your data and try again.',
  'errors.invalidInputArguments':
    'Invalid input arguments. Please check your request and try again.',

  'errors.patientDataNotFound':
    'Patient data could not be found. Please verify the patient information and try again.',
  'errors.patientNotFound':
    'Patient not found. Please verify the patient information and try again.',
  'errors.patientExists':
    'A patient with this information already exists. Please check the patient details.',
  'errors.getPatientError':
    'Failed to retrieve patient information. Please try again or contact support.',
  'errors.getPatientsError': 'Failed to retrieve patients. Please try again or contact support.',
  'errors.addPatientError': 'Failed to add patient. Please check the data and try again.',
  'errors.editPatientError':
    'Failed to update patient information. Please check the data and try again.',
  'errors.failedToUpdatePatient':
    'Failed to update patient information. Please check the data and try again. If the problem persists, contact support.',
  'errors.patientOnboardError': 'Failed to onboard patient. Please try again or contact support.',
  'errors.patientStatusNotFound': 'Patient status not found. Please contact support.',
  'errors.patientStatusError': 'An error occurred with patient status. Please try again.',
  'errors.patientStatusHistoryNotFound': 'Patient status history not found.',
  'errors.patientExternalIdNotFound':
    'Patient external ID not found. Please verify the patient information.',
  'errors.failedToGetPatientsData': 'Failed to retrieve patient data. Please try again.',
  'errors.lookupPatientError': 'Failed to lookup patient. Please try again.',
  'errors.failedToGetDuplicatePatients': 'Failed to retrieve duplicate patients. Please try again.',

  'errors.patientAddressNotFound':
    'Patient address not found. Please verify the address information.',
  'errors.patientAddressIsDefault':
    'This address is set as default and cannot be modified in this way.',
  'errors.patientDefaultAddressCannotBeDeleted':
    'The default address cannot be deleted. Please set another address as default first.',
  'errors.addPatientAddressError':
    'Failed to add patient address. Please check the address details and try again.',
  'errors.getPatientAddressError': 'Failed to retrieve patient address. Please try again.',
  'errors.deletePatientAddressError': 'Failed to delete patient address. Please try again.',
  'errors.addressNotFound': 'Address not found. Please verify the address information.',
  'errors.addressDirectoryNotFound': 'Address directory not found. Please contact support.',
  'errors.addressNotFoundInDirectory': 'Address not found in directory. Please verify the address.',
  // POM-6318: none of the Fill Later codes were mapped, so every refusal the server worded
  // carefully arrived as the generic message. The queue in particular refuses on rows that have
  // moved on since the page loaded, where "Request failed" tells the operator nothing about what
  // to do next.
  'errors.fillLaterItemNotFound':
    'This fill later record no longer exists. Refresh the queue and try again.',
  'errors.fillLaterItemNotActionable':
    'This fill later has already moved on and can no longer be changed. Refresh the queue to see its current state.',
  'errors.fillLaterItemNotMergeable':
    'This fill later can no longer be merged back onto its order. Refresh the queue and try again.',
  'errors.fillLaterItemNotCancellable':
    'This fill later can no longer be cancelled. Refresh the queue to see its current state.',
  'errors.fillLaterOrderNotFound':
    'The order this fill later belongs to could not be found. Please contact support.',
  'errors.orderNotEligibleForFillLater':
    'This order can no longer be moved to fill later. Refresh the page to see its current state.',
  'errors.invalidFillLaterSplitQuantity':
    'The quantity to fill later is not valid for this line item. Please check it and try again.',
  'errors.invalidFillLaterMoveDate':
    'The fill date is not valid. Pick a date after today and within one year.',
  'errors.failedToFetchFillLaterItems':
    'Failed to load the fill later queue. Please try again or contact support.',
  'errors.failedToRescheduleFillLaterItem':
    'Failed to change the fill date. Please try again or contact support.',
  'errors.failedToUpdateFillLaterAddress':
    'Failed to change the shipping address. Please try again or contact support.',
  'errors.failedToMergeFillLaterItem':
    'Failed to merge this fill later back onto its order. Please try again or contact support.',
  'errors.failedToCancelFillLaterItem':
    'Failed to cancel this fill later. Please try again or contact support.',
  'errors.failedToMoveLineItemsToFillLater':
    'Failed to move these items to fill later. Please try again or contact support.',
  'errors.failedToMoveOrderToFillLater':
    'Failed to move this order to fill later. Please try again or contact support.',
  'errors.failedToSplitLineItemQuantity':
    'Failed to schedule the split quantity. Please try again or contact support.',
  'errors.splitLineItemNotFound':
    'That line item is no longer on this order. Refresh the page and try again.',
  'errors.lineItemNotEligibleForSplit':
    'This line item can no longer be split. It has moved past product fulfillment, or it is under review.',
  'errors.lineItemNotEligibleForMove':
    'This line item can no longer be moved to fill later. Refresh the page to see its current state.',
  'errors.splitExceedsDispenseQuantity':
    'The quantities scheduled to fill later leave nothing to dispense now. Reduce them and try again.',
  'errors.duplicateLineItemInMove':
    'The same line item was sent twice. Refresh the page and try again.',
  'errors.lineItemOutsidePharmacyScope':
    'This line item belongs to another fulfilling pharmacy, so it cannot be changed here.',
  'errors.invalidFillDate':
    'The fill date is not valid. Pick a date after today and within one year.',
  'errors.addressNotOnPatientRecord':
    "That shipping address is not on this patient's record. Pick one of the patient's addresses and try again.",
  'errors.shippingAddressNotValid':
    'The shipping address is not valid. Please check the address details.',
  'errors.missingShippingAddressDirectoryId':
    'Shipping address directory ID is missing. Please provide a valid address.',
  'errors.addressExceedsFedexLabelLimits':
    'This address is invalid. Please change the address before generating a shipping label.',
  'errors.maxCombinedAddressLengthError':
    'Address Line 1 and Address Line 2 combined must be 70 characters or fewer. Please shorten the address and try again.',

  'errors.patientInsuranceNotFound':
    'Patient insurance not found. Please verify the insurance information.',
  'errors.addInsuranceError': 'Failed to add insurance. Please check the data and try again.',
  'errors.updateInsuranceError': 'Failed to update insurance. Please check the data and try again.',
  'errors.deactivatePatientInsuranceFailed':
    'Failed to deactivate patient insurance. Please try again.',
  'errors.inActiveInsurance':
    'The insurance is inactive. Please activate it or use a different insurance.',
  'errors.maxBinLengthError':
    'Insurance BIN number exceeds maximum length. Please check the BIN number.',
  'errors.maxGroupNumberLengthError':
    'Insurance group number exceeds maximum length. Please check the group number.',
  'errors.maxInsurancePlanIdLengthError':
    'Insurance plan ID exceeds maximum length. Please check the plan ID.',

  'errors.patientPharmacyNotFound':
    'Patient pharmacy not found. Please verify the pharmacy information.',
  'errors.patientPharmacyExceededMaximumAssociation':
    'Patient has exceeded the maximum number of pharmacy associations.',
  'errors.getPatientPharmacyError': 'Failed to retrieve patient pharmacy. Please try again.',
  'errors.addPatientPharmacyError': 'Failed to add patient pharmacy. Please try again.',
  'errors.deletePatientPharmacyError': 'Failed to delete patient pharmacy. Please try again.',
  'errors.updatePatientPharmacyError': 'Failed to update patient pharmacy. Please try again.',
  'errors.failedToGetPatientPharmacy': 'Failed to retrieve patient pharmacy. Please try again.',

  'errors.patientProviderNotFound':
    'Patient provider not found. Please verify the provider information.',
  'errors.updatePatientProviderError': 'Failed to update patient provider. Please try again.',
  'errors.getPatientProvidersError': 'Failed to retrieve patient providers. Please try again.',
  'errors.failedToAddProvider': 'Failed to add provider. Please try again.',
  'errors.failedToFindExistingProvider': 'Failed to find existing provider. Please try again.',
  'errors.failedToDeactivateProvider': 'Failed to deactivate provider. Please try again.',
  'errors.failedToAddProviderInMedication':
    'Failed to add provider in medication. Please try again.',
  'errors.addProviderError': 'Failed to add provider. Please try again.',
  'errors.deactivateProviderError': 'Failed to deactivate provider. Please try again.',
  'errors.providerNotFound': 'Provider not found. Please verify the provider information.',

  'errors.patientMedicationNotFound':
    'Patient medication not found. Please verify the medication information.',
  'errors.failedToGetListPatientMedication':
    'Failed to retrieve patient medications. Please try again.',
  'errors.failedToAddMedication': 'Failed to add medication. Please try again.',
  'errors.failedToAddPharmacyInMedication':
    'Failed to add pharmacy in medication. Please try again.',
  'errors.failedToDeleteMedication': 'Failed to delete medication. Please try again.',
  'errors.updatePatientMedicationError': 'Failed to update patient medication. Please try again.',

  'errors.patientGuarantorNotFound':
    'Patient guarantor not found. Please verify the guarantor information.',
  'errors.guarantorNotFound': 'Guarantor not found. Please verify the guarantor information.',
  'errors.addPatientGuarantorFailed': 'Failed to add patient guarantor. Please try again.',
  'errors.addPatientGuarantorError': 'Failed to add patient guarantor. Please try again.',
  'errors.updatePatientGuarantorFailed': 'Failed to update patient guarantor. Please try again.',
  'errors.deletePatientGuarantorError': 'Failed to delete patient guarantor. Please try again.',
  'errors.failedToGetGuarantors': 'Failed to retrieve guarantors. Please try again.',
  'errors.getPatientGuarantorError': 'Failed to retrieve patient guarantor. Please try again.',
  'errors.syncGuarantorFailed': 'Failed to sync guarantor. Please try again.',
  'errors.syncGuarantorError': 'Failed to sync guarantor. Please try again.',
  'errors.syncPatientGuarantorFailed': 'Failed to sync patient guarantor. Please try again.',
  'errors.syncPatientGuarantorError': 'Failed to sync patient guarantor. Please try again.',

  'errors.patientAuthorizationNotFound':
    'Patient authorization not found. Please verify the authorization information.',
  'errors.updatePatientAuthorizationError':
    'Failed to update patient authorization. Please try again.',
  'errors.getPatientAuthorizationError':
    'Failed to retrieve patient authorization. Please try again.',
  'errors.getAuthorizerDemographicsError':
    'Failed to retrieve authorizer demographics. Please try again.',
  'errors.failedToBuildPatientAuthorization':
    'Failed to build patient authorization. Please try again.',

  'errors.patientPartnerNotFound':
    'Patient partner not found. Please verify the partner information.',
  'errors.patientPartnerError': 'An error occurred with patient partner. Please try again.',
  'errors.addPatientPartnerError': 'Failed to add patient partner. Please try again.',
  'errors.deletePatientPartnerFailed': 'Failed to delete patient partner. Please try again.',
  'errors.setPartnerExternalIdError': 'Failed to set partner external ID. Please try again.',
  'errors.patientPartnerAssociationAlreadyExists': 'Patient partner association already exists.',
  'errors.patientPartnerPracticeExists': 'Patient partner practice association already exists.',
  'errors.failedToSavePatientPartnerAssociations':
    'Failed to save patient partner associations. Please try again.',
  'errors.syncPatientPartnerError': 'Failed to sync patient partner. Please try again.',
  'errors.deleteOriginalPartnerNotAllowed': 'Deleting the original partner is not allowed.',

  'errors.patientRequestNotFound':
    'Patient request not found. Please verify the request information.',
  'errors.listPatientRequestsError': 'Failed to list patient requests. Please try again.',
  'errors.listPatientOpenRequestsError': 'Failed to list patient open requests. Please try again.',
  'errors.getAllPatientsRequestError': 'Failed to retrieve all patient requests. Please try again.',
  'errors.requestNotFound': 'Request not found. Please verify the request information.',
  'errors.requestLineItemNotFound':
    'Request line item not found. Please verify the line item information.',
  'errors.failedToFetchRequestLineItem': 'Failed to fetch request line item. Please try again.',
  'errors.getRxRequestLineItemError': 'Failed to retrieve RX request line item. Please try again.',
  'errors.failedToSyncRequestLineItem': 'Failed to sync request line item. Please try again.',
  'errors.errorCreatingPatientRequest': 'Failed to create patient request. Please try again.',

  'errors.orderNotFound':
    "The order you're trying to access doesn't exist. Please check the order reference ID and try again.",
  'errors.verifyOrderError': 'Order verification failed. Please try again.',
  'errors.orderReferenceIdAlreadyExist':
    'An order with this reference ID already exists. Please use a different reference ID or contact support.',
  'errors.orderProgressive':
    'This order has already moved forward in the processing or shipping stage and can no longer be modified. For further assistance, please contact support.',
  'errors.orderProgressed':
    'This order has already moved forward in the processing or shipping stage and can no longer be modified. For further assistance, please contact support.',
  'errors.orderIsInProgressed':
    'This order has already moved forward in the processing or shipping stage and can no longer be modified. For further assistance, please contact support.',
  'errors.orderAlreadyCancelled':
    'This order has already been cancelled and cannot be modified. For further assistance, please contact support.',
  'errors.orderIsInCancelledState':
    'This order has already been cancelled and cannot be modified. For further assistance, please contact support.',
  'errors.cancelOrderError': 'Failed to cancel order. Please try again or contact support.',
  'errors.orderStateError':
    'This order can no longer be modified or processed. For further assistance, please contact support.',
  'errors.invalidOrderStatus': 'Invalid order status. Please verify the order status.',
  'errors.orderIsNotInAppropriateStateForTransfer':
    'Order is not in an appropriate state for transfer.',
  'errors.orderVerifiedError': 'Failed to verify order. Please try again.',
  'errors.errorProcessingOrder':
    'An error occurred while processing the order. Please try again or contact support.',
  'errors.errorTransferringOrder': 'Failed to transfer order. Please try again.',
  'errors.failedToUpdateOrderAddress': 'Failed to update order address. Please try again.',
  'errors.failedToUpdateOrderAssociation': 'Failed to update order association. Please try again.',
  'errors.failedToUpdateOrderShippingDetails':
    'Failed to update order shipping details. Please try again.',
  'errors.createRxOrderError': 'Failed to create RX order. Please try again.',
  'errors.failedToFetchPatientOrders': 'Failed to fetch patient orders. Please try again.',
  'errors.failedToFetchOrderLineItems': 'Failed to fetch order line items. Please try again.',
  'errors.failedToListOrders': 'Failed to list orders. Please try again.',
  'errors.errorListingApiOrders': 'Failed to list API orders. Please try again.',
  'errors.errorRetrievingPomOrders': 'Failed to retrieve orders. Please try again.',
  'errors.orderShipmentNotFound':
    'Order shipment not found. Please verify the shipment information.',
  'errors.orderShipmentDetailsNotFound':
    'Order shipment details not found. Please verify the shipment information.',
  'errors.createOrderShipmentError': 'Failed to create order shipment. Please try again.',
  'errors.lineItemsNotReadyForShipment':
    'The selected item(s) are already in shipment or not ready to ship. Refresh the page to see their current state.',
  'errors.concurrentShipmentError':
    'Another user is already processing this shipment. Please wait a moment and refresh the page.',
  'errors.deleteOrderShipmentError': 'Failed to delete order shipment. Please try again.',
  'errors.editOrderShipmentError': 'Failed to edit order shipment. Please try again.',
  'errors.failedToFetchPatientOrderShipment':
    'Failed to fetch patient order shipment. Please try again.',
  'errors.cannotPerformCantFill':
    'Cannot perform "can\'t fill" action. Please verify the order state.',
  'errors.orderPatientAssociationNotFound': 'Order patient association not found.',
  'errors.patientOrderRelationNotFound': 'Patient order relation not found.',
  'errors.moveLineItemToNewOrder': 'Line item needs to be moved to a new order.',

  'errors.errorImportingOrders':
    'Failed to import the order. Please check the order data and try again. If the problem persists, contact support.',
  'errors.escriptOrderImportInProgress':
    'This order is currently being processed by another user. Please try again later.',
  'errors.orderAlreadyImported': 'This order has already been imported',
  'errors.orderImportFailed':
    'Failed to import the order. Please check the order data and try again. If the problem persists, contact support.',
  'errors.errorRetrievingImportConfig':
    'Failed to retrieve import configuration. Please try again.',
  'errors.errorListingOrderImportHistory': 'Failed to list order import history. Please try again.',
  'errors.errorGettingOrderImportConfigs':
    'Failed to get order import configurations. Please try again.',

  'errors.prescriptionNotFoundError':
    'Prescription not found. Please verify the prescription information.',
  'errors.editPrescriptionError': 'Failed to edit prescription. Please try again.',
  'errors.prescriptionHasExpired': 'This prescription has expired and cannot be used.',
  'errors.prescriptionUuidNotExistError':
    'Prescription UUID does not exist. Please verify the prescription information.',
  'errors.prescriptionReviewNotAllowed': 'Prescription review is not allowed at this time.',
  'errors.prescriptionAttestationLinkAlreadyExist': 'Prescription attestation link already exists.',
  'errors.failedToReviewPrescription': 'Failed to review prescription. Please try again.',
  'errors.failedToEditPrescription': 'Failed to edit prescription. Please try again.',
  'errors.failedToMarkPrescriptionForReview':
    'Failed to mark prescription for review. Please try again.',
  'errors.failedToMarkChangeReviewReason': 'Failed to mark change review reason. Please try again.',
  'errors.failedToChangePrescriptionReasonForReview':
    'Failed to change prescription reason for review. Please try again.',
  'errors.errorResolvingDuplicatePrescription':
    'Failed to resolve duplicate prescription. Please try again.',
  'errors.listPatientPrescriptionError': 'Failed to list patient prescriptions. Please try again.',
  'errors.refreshPatientPrescriptionError':
    'Failed to refresh patient prescription. Please try again.',
  'errors.savePatientPrescriptionError': 'Failed to save patient prescription. Please try again.',
  'errors.createPrescriptionFromQs1Error':
    'Failed to create prescription from QS1. Please try again.',
  'errors.failedToSavePrescriptionOverrideRecord':
    'Failed to save prescription override record. Please try again.',
  'errors.failedToUpdatePrescriptionOverrideRecord':
    'Failed to update prescription override record. Please try again.',
  'errors.failedToGetPrescriptionType': 'Failed to get prescription type. Please try again.',
  'errors.failedToValidatePrescriptionInput':
    'Failed to validate prescription input. Please check the data and try again.',
  'errors.failedToValidatePrescriptionForRefill':
    'Failed to validate prescription for refill. Please try again.',
  'errors.failedToListAdjudicationFailedPrescriptions':
    'Failed to list adjudication failed prescriptions. Please try again.',

  'errors.rxReferenceNotFound':
    "We couldn't find the associated order for the provided reference ID and patient ID combination. Please check your input and try again.",
  'errors.rxNumberNotFound': 'RX number not found. Please verify the RX number.',
  'errors.rxLineItemNotFound': 'RX line item not found. Please verify the line item information.',
  'errors.rxOrderNotFound': 'RX order not found. Please verify the order information.',
  'errors.failedToFetchRxOrder': 'Failed to fetch RX order. Please try again.',
  'errors.failedToRxProfileError': 'Failed to retrieve RX profile. Please try again.',
  'errors.failedToGenerateUniqueRxNumber': 'Failed to generate unique RX number. Please try again.',
  'errors.identifiedRxNumberCollision': 'RX number collision detected. Please contact support.',
  'errors.qs1RxNumberLengthError': 'QS1 RX number length error. Please check the RX number.',

  'errors.lineItemNotFound': 'Line item not found. Please verify the line item information.',
  'errors.lineItemHasMissingInformation':
    'Line item has missing information. Please complete all required fields.',
  'errors.lineItemShouldBeInRxEntryState': 'Line item should be in RX entry state.',
  'errors.lineItemStateTransitionFailed': 'Line item state transition failed. Please try again.',
  'errors.invalidLineItemStatus': 'Invalid line item status. Please verify the status.',
  'errors.getLineItemsTaggedForReviewError':
    'Failed to get line items tagged for review. Please try again.',
  'errors.failedToTagItemForReview': 'Failed to tag item for review. Please try again.',
  'errors.failedToCancelLineItem': 'Failed to cancel line item. Please try again.',
  'errors.failedToMoveLineItemToProductFulfillment':
    'Failed to move line item to product fulfillment. Please try again.',
  'errors.failedToAddressOrderLineItem': 'Failed to address order line item. Please try again.',
  'errors.failedToUpdateAddressOrderLineItems':
    'Failed to update address order line items. Please try again.',
  'errors.failedToFetchLineItemRecord': 'Failed to fetch line item record. Please try again.',
  'errors.failedToUpdateLineItemTags': 'Failed to update line item tags. Please try again.',
  'errors.failedToListLineItemTags': 'Failed to list line item tags. Please try again.',
  'errors.failedToValidateLineItems': 'Failed to validate line items. Please try again.',
  'errors.reviewLineItemError': 'Failed to review line item. Please try again.',
  'errors.verifyLineItemError': 'Failed to verify line item. Please try again.',
  'errors.getRequestLineItemVerificationData':
    'Failed to get request line item verification data. Please try again.',
  'errors.noAssociationFoundForLineItemAndPrescription':
    'No association found for line item and prescription.',
  'errors.failedToUpdateAddOnQuantity':
    'Failed to update add-on quantity. Please try again or contact support.',

  'errors.noMatchingAddressFound':
    'The entered address could not be verified. Please check the details and try again.',
  'errors.failedToValidateAddress':
    'The address could not be validated. Please check the address details and try again.',
  'errors.externalIdAlreadyExists':
    'An external ID with this value already exists. Please use a different external ID or contact support.',
  'errors.noIdpIdFromToken':
    'Unable to retrieve user identity from the authentication token. Please try logging in again. If the problem persists, contact support.',

  'errors.addressValidationFailed':
    'Address validation failed. Please check the address details and try again.',
  'errors.alreadyScanned': 'This item has already been scanned. Please verify the item.',
  'errors.itemLockedForPhysicalCount':
    'This inventory item is locked for a physical count. Adjustments are not allowed until the count is complete. Please try again once the count has been finalized.',
  'errors.badExpectedFieldsInputError':
    'Invalid input fields provided. Please check your input and try again.',
  'errors.clientConfigNotFound': 'Client configuration not found. Please contact support.',
  'errors.createUserError': 'Failed to create user. Please try again or contact support.',
  'errors.createUserWithPatientLinkError':
    'Failed to create user with patient link. Please try again or contact support.',
  'errors.dateRangeExceeded':
    'The date range exceeds the maximum allowed period. Please select a shorter date range.',
  'errors.errorCreatingShipment':
    'Failed to create shipment. Please check the shipment details and try again.',
  'errors.exceedsValidationAttemptsError':
    'Too many validation attempts. Please try again later or contact support.',
  'errors.existingPatientLinkError':
    'A patient link already exists. Please verify the patient information.',
  'errors.failedAddGuarantorToPatient':
    'Failed to add guarantor to patient. Please check the guarantor information and try again.',
  'errors.failedCreatePatient':
    'Failed to create patient. Please check the patient data and try again.',
  'errors.failedCreatePatientHistory':
    'Failed to create patient history. Please try again or contact support.',
  'errors.failedGetPartnerMetaData':
    'Failed to get partner metadata. Please try again or contact support.',
  'errors.failedOtcTransfer': 'Failed to transfer OTC order. Please try again.',
  'errors.failedPartnerCreation': 'Failed to create partner. Please try again or contact support.',
  'errors.failedPatientAuthorization':
    'Failed to authorize patient. Please verify the patient information and try again.',
  'errors.failedRefillPrescription':
    'Failed to refill prescription. Please check the prescription details and try again.',
  'errors.failedRxTransfer': 'Failed to transfer RX. Please try again or contact support.',
  'errors.failedToAddMessageToQueue':
    'Failed to add message to queue. Please try again or contact support.',
  'errors.failedToAddPatientInsurance':
    'Failed to add patient insurance. Please check the insurance information and try again.',
  'errors.failedToCheckExistingPrescription':
    'Failed to check existing prescription. Please try again or contact support.',
  'errors.failedToConstructPayload':
    'Failed to construct payload. Please try again or contact support.',
  'errors.failedToCreateOrder':
    'Failed to create order. Please check the order details and try again.',
  'errors.failedToCreateOtcOrder':
    'Failed to create OTC order. Please check the order details and try again.',
  'errors.failedToCreatePartnerUser':
    'Failed to create partner user. Please check the user information and try again.',
  'errors.failedToCreatePrescription':
    'Failed to create prescription. Please check the prescription details and try again.',
  'errors.failedToCreatePracticeProvider':
    'Failed to create practice provider. Please check the provider information and try again.',
  'errors.failedToCreateRefillPrescription':
    'Failed to create refill prescription. Please check the prescription details and try again.',
  'errors.failedToEditPracticeUser':
    'Failed to edit practice user. Please check the user information and try again.',
  'errors.failedToFetchMedications':
    'Failed to fetch medications. Please try again or contact support.',
  'errors.failedToFetchPartnerCredentials':
    'Failed to fetch partner credentials. Please try again or contact support.',
  'errors.failedToFetchPartnerCrmIssues':
    'Failed to fetch partner CRM issues. Please try again or contact support.',
  'errors.failedToFetchPartnerMetaData':
    'Failed to fetch partner metadata. Please try again or contact support.',
  'errors.failedToFetchPatient': 'Failed to fetch patient. Please try again or contact support.',
  'errors.failedToFetchUser': 'Failed to fetch user. Please try again or contact support.',
  'errors.failedToGenerateToken': 'Failed to generate token. Please try again or contact support.',
  'errors.failedToGetAccessToken':
    'Failed to get access token. Please try again or contact support.',
  'errors.failedToGetProvider': 'Failed to get provider. Please try again or contact support.',
  'errors.failedToListDrugProfiles':
    'Failed to list drug profiles. Please try again or contact support.',
  'errors.failedToRegisterWebhook':
    'Failed to register webhook. Please try again or contact support.',
  'errors.failedToRetrievePracticeFromCache':
    'Failed to retrieve practice from cache. Please try again or contact support.',
  'errors.failedToScanDynamoDb': 'Failed to scan DynamoDB. Please try again or contact support.',
  'errors.failedToSyncPartnerStatusUpdate':
    'Failed to sync partner status update. Please try again or contact support.',
  'errors.failedToTransferPrescription':
    'Failed to transfer prescription. Please try again or contact support.',
  'errors.failedToTransferPrescriptionToQueue':
    'Failed to transfer prescription to queue. Please try again or contact support.',
  'errors.failedToUpdateBroadcastMessage':
    'Failed to update broadcast message. Please try again or contact support.',
  'errors.failedToUpdateOtcRequest':
    'Failed to update OTC request. Please try again or contact support.',
  'errors.failedToUpdatePartnerCrmIssueStatus':
    'Failed to update partner CRM issue status. Please try again or contact support.',
  'errors.failedToUpdatePartnerUser':
    'Failed to update partner user. Please check the user information and try again.',
  'errors.failedToUpdateUserAuthCache':
    'Failed to update user auth cache. Please try again or contact support.',
  'errors.failedUpdatePatientRequest':
    'Failed to update patient request. Please try again or contact support.',
  'errors.futureDate':
    'A future date was provided. Please select a valid date that is not in the future.',
  'errors.getPartnerApiAccessError':
    'Failed to get partner API access. Please try again or contact support.',
  'errors.inactivePartner':
    'The partner is inactive. Please activate the partner or contact support.',
  'errors.inactivePractice':
    'The practice is inactive. Please activate the practice or contact support.',
  'errors.ineligiblePrescriptionForRefill':
    'This prescription is not eligible for refill. Please verify the prescription details.',
  'errors.invalidOrderId': 'Invalid order ID provided. Please verify the order ID and try again.',
  'errors.invalidPayload':
    'Invalid payload provided. Please check your request data and try again.',
  'errors.invalidRequest': 'Invalid request. Please check your request and try again.',
  'errors.mandatoryFieldMissing':
    'A mandatory field is missing. Please complete all required fields and try again.',
  'errors.medicationNotFound': 'Medication not found. Please verify the medication information.',
  'errors.mobileNumberRequired':
    'Mobile number is required. Please provide a mobile number and try again.',
  'errors.noDataFound': 'No data found. Please verify your search criteria and try again.',
  'errors.noFormulariesFound':
    'No formularies found. Please contact support or verify the formulary configuration.',
  'errors.partnerBiDashboardAlreadyExists':
    'Partner BI dashboard already exists. Please use a different configuration or contact support.',
  'errors.partnerConfigNotFound':
    'Partner configuration not found. Please verify the partner configuration or contact support.',
  'errors.partnerProviderAssociationNotFound':
    'Partner provider association not found. Please verify the association.',
  'errors.partnerUserMappingNotFound':
    'Partner user mapping not found. Please verify the user mapping or contact support.',
  'errors.patientEventDataValidationError':
    'Patient event data validation failed. Please check the event data and try again.',
  'errors.patientLinkingError':
    'Failed to link patient. Please verify the patient information and try again.',
  'errors.patientLookupError':
    'Failed to lookup patient. Please verify the patient information and try again.',
  'errors.paymentFailed':
    'Payment failed. Please check your payment method and try again. If the problem persists, contact support.',
  'errors.paymentLessThanMinimumBalanceError':
    'Payment amount is less than the minimum balance required. Please adjust the payment amount.',
  'errors.phoneNumberNotVerified':
    'Phone number is not verified. Please verify your phone number and try again.',
  'errors.policyNotAcceptedError':
    'Policies were not accepted. Please accept the required policies to continue.',
  'errors.practiceProviderAssociationNotFound':
    'Practice provider association not found. Please verify the association.',
  'errors.practiceUserAssociationNotFound':
    'Practice user association not found. Please verify the association.',
  'errors.reconciliationFailedError': 'Reconciliation failed. Please try again or contact support.',
  'errors.resolveDrugProfile':
    'Failed to resolve drug profile. Please try again or contact support.',
  'errors.setPomStatusError': 'Failed to set POM status. Please try again or contact support.',
  'errors.shipmentAlreadyExists':
    'A shipment with this information already exists. Please use different information or contact support.',
  'errors.smsProviderEventValidationError':
    'SMS provider event validation failed. Please check the event data and try again.',
  'errors.userSelfLinkExistError':
    'User self-link already exists. Please verify the user link information.',

  'error.dateRangeExceeded':
    'The date range exceeds the maximum allowed period. Please select a shorter date range.',
  'error.futureDate':
    'A future date was provided. Please select a valid date that is not in the future.',
  'error.paymentFailed':
    'Payment failed. Please check your payment method and try again. If the problem persists, contact support.',
  'error.policyNotAcceptedError':
    'Policies were not accepted. Please accept the required policies to continue.',
  'error.phoneNumberNotVerified':
    'Phone number is not verified. Please verify your phone number and try again.',
  'error.userNotFound': 'User not found. Please verify the user information.',

  'errors.failedToFetchShippingOptions':
    'Unable to retrieve shipping options at this time. Please try again later or contact support.',
  'errors.failedToFetchBatchShipments': 'Failed to fetch batch shipments. Please try again.',
  'errors.fetchBatchShipmentsError': 'Failed to fetch batch shipments. Please try again.',
  'errors.transformBatchShipmentsError': 'Failed to transform batch shipments. Please try again.',
  'errors.enrichShipmentError': 'Failed to enrich shipment. Please try again.',
  'errors.voidShipmentError': 'Failed to void shipment. Please try again.',
  'errors.syncShippedShipmentsError': 'Failed to sync shipped shipments. Please try again.',

  'errors.failedLabelCreation': 'Failed to create label. Please try again.',
  'errors.failedBulkLabelGeneration': 'Failed to generate bulk labels. Please try again.',
  'errors.failedBulkLabelCreation': 'Failed to create bulk labels. Please try again.',
  'errors.failedFetchLabel': 'Failed to fetch label. Please try again.',
  'errors.failedToFetchBulkLabels': 'Failed to fetch bulk labels. Please try again.',
  'errors.failedToMarkLineItemsAsPrinted':
    'Failed to mark line items as printed. Please try again.',
  'errors.rxLabelNotFound': 'RX label not found. Please verify the label information.',
  'errors.labelNotFound': 'Label not found. Please verify the label information.',
  'errors.invalidLabelVersion': 'Invalid label version. Please contact support.',
  'errors.failedToListBulkLabelDownloadHistory':
    'Failed to list bulk label download history. Please try again.',

  'errors.userEmailInUse':
    'This email address is already in use. Please use a different email address.',
  'errors.userNotFound': 'User not found. Please verify the user information.',
  'errors.peUserNotFound': 'PE user not found. Please verify the user information.',
  'errors.getUserInfo': 'Failed to retrieve user info. Please try again.',
  'errors.failedToUpdateTheUser': 'Failed to update user. Please try again.',
  'errors.failedToFetchUsers': 'Failed to fetch users. Please try again.',
  'errors.failedToManageUserAccount': 'Failed to manage user account. Please try again.',
  'errors.getPatientUserError': 'Failed to get patient user. Please try again.',
  'errors.getPatientUsersFromMaapError': 'Failed to get patient users from MAAP. Please try again.',
  'errors.userPatientLinkNotFound': 'User patient link not found.',
  'errors.userNotAssociatedWithSalesGroup': 'User is not associated with a sales group.',
  'errors.failedToFetchPomUser': 'Failed to fetch user. Please try again.',
  'errors.failedToListPomUsers': 'Failed to list users. Please try again.',
  'errors.failedToGetPomSystemUser': 'Failed to get system user. Please try again.',
  'errors.cognitoUserDetailsNotFound': 'Cognito user details not found. Please contact support.',
  'errors.failedToTriggerForgotPasswordForPeUser':
    'Failed to trigger forgot password for PE user. Please try again.',

  'errors.partnerNotFound': 'Partner not found. Please verify the partner information.',
  'errors.partnerWithSameDataAlreadyExists':
    'A partner with the same data already exists. Please use different information.',
  'errors.partnerExternalIdExists':
    'Partner external ID already exists. Please use a different external ID.',
  'errors.partnerFulfillingPharmacyNotFound': 'Partner fulfilling pharmacy not found.',
  'errors.getPartnersError': 'Failed to get partners. Please try again.',
  'errors.getPartnersFailed': 'Failed to get partners. Please try again.',
  'errors.partnerPracticeAssociationNotFound': 'Partner practice association not found.',
  'errors.partnerOnboardingDetailsNotExists': 'Partner onboarding details do not exist.',
  'errors.partnerOnboardingAlreadyCompleted': 'Partner onboarding has already been completed.',
  'errors.partnerOnboardingInvitationAlreadyFound': 'Partner onboarding invitation already found.',
  'errors.failedToGetPartnerOnboardingDetails':
    'Failed to get partner onboarding details. Please try again.',
  'errors.failedToApprovePartnerOnboardingForm':
    'Failed to approve partner onboarding form. Please try again.',
  'errors.failedToSentOnboardingInvite': 'Failed to send onboarding invite. Please try again.',
  'errors.failedToResentOnboardingInvite': 'Failed to resend onboarding invite. Please try again.',
  'errors.failedToSentPartnerToReview': 'Failed to send partner to review. Please try again.',
  'errors.partnerAutoAuthorizationConfigDisabled': 'Partner auto authorization config is disabled.',
  'errors.partnerWorkflowConfigError':
    'Partner workflow configuration error. Please contact support.',
  'errors.savePartnerWorkflowConfigError':
    'Failed to save partner workflow configuration. Please try again.',
  'errors.getPartnerWorkFlowError': 'Failed to get partner workflow. Please try again.',
  'errors.failedToFetchGetPartnerWorkflowConfigs':
    'Failed to fetch partner workflow configurations. Please try again.',
  'errors.failedToFetchPartnerConfig': 'Failed to fetch partner configuration. Please try again.',
  'errors.failedToUpdatePartnerNotificationConfig':
    'Failed to update partner notification configuration. Please try again.',
  'errors.failedToUpdatePartnerCatalogDefaults': 'Unable to update catalog config. Try again.',
  'errors.failedToUpdatePartnerCatalogStatus':
    'Unable to update the status for ordering. Try again.',
  'errors.failedToUpdatePartnerIssueConfig':
    'Failed to update partner issue configuration. Please try again.',
  'errors.failedToUpdatePartnerFormulary': 'Failed to update partner formulary. Please try again.',
  'errors.failedToUpdatePartnerOtcCatalog':
    'Failed to update partner OTC catalog. Please try again.',
  'errors.failedToAddPartnerWithApiAccess':
    'Failed to add partner with API access. Please try again.',
  'errors.partnerDoesNotHaveZendeskIntegration':
    'Partner does not have Zendesk integration configured.',
  'errors.errorGetPartnerPractices': 'Failed to get partner practices. Please try again.',
  'errors.partnerDiscountCardNotFound': 'Partner discount card not found.',
  'errors.getPartnerDiscountCardFailed': 'Failed to get partner discount card. Please try again.',
  'errors.errorGettingPartnerOtcCatalog': 'Failed to get partner OTC catalog. Please try again.',

  'errors.practiceNotFound': 'Practice not found. Please verify the practice information.',
  'errors.practiceNameAlreadyExists':
    'A practice with this name already exists. Please use a different name.',
  'errors.failedToUpdatePractice': 'Failed to update practice. Please try again.',
  'errors.failedToGetPractice': 'Failed to get practice. Please try again.',
  'errors.failedToGetPracticeData': 'Failed to get practice data. Please try again.',
  'errors.failedToGetPartnerPracticeData': 'Failed to get partner practice data. Please try again.',
  'errors.failedToUpdatePracticeNotificationConfig':
    'Failed to update practice notification configuration. Please try again.',
  'errors.failedToFetchPracticeProviders': 'Failed to fetch practice providers. Please try again.',

  'errors.fulfillingPharmacyNotFound':
    'Fulfilling pharmacy not found. Please verify the pharmacy information.',
  'errors.failedToFetchFulfillingPharmacy':
    'Failed to fetch fulfilling pharmacy. Please try again.',
  'errors.failedToFetchFulfillingPharmacyShipmentDetails':
    'Failed to fetch fulfilling pharmacy shipment details. Please try again.',
  'errors.formularyNotFound': 'Formulary not found. Please verify the formulary information.',
  'errors.formularyAlreadyExists': 'Formulary already exists. Please use a different formulary.',
  'errors.formularyDeleted': 'Formulary has been deleted.',
  'errors.formularyNotActive': 'Formulary is not active.',
  'errors.formularyUnavailableForPartner': 'Formulary is unavailable for this partner.',
  'errors.failedToCreateFormulary': 'Failed to create formulary. Please try again.',
  'errors.failedToUpdateFormulary': 'Failed to update formulary. Please try again.',
  'errors.failedToListFormulary': 'Failed to list formulary. Please try again.',
  'errors.failedToFetchPartnerFormulary': 'Failed to fetch partner formulary. Please try again.',
  // Catalog error codes (formulary→catalog rename in smartphos-core). The formulary* keys above are
  // kept so the UI still resolves any FORMULARY_* code a backend may still emit.
  'errors.catalogNotFound': 'Catalog not found. Please verify the catalog information.',
  'errors.catalogAlreadyExists': 'Catalog already exists. Please use a different catalog.',
  'errors.catalogDeleted':
    'The selected catalog is deleted or inactive and cannot be added or updated. Please choose an active catalog.',
  'errors.catalogNotActive': 'Catalog is not active.',
  'errors.catalogUnavailableForPartner': 'Catalog is unavailable for this partner.',
  'errors.catalogMissingProductId':
    'This catalog item is missing a product ID. Please update the catalog and try again.',
  'errors.noCatalogsFound':
    'No catalogs found. Please contact support or verify the catalog configuration.',
  'errors.failedToFetchCatalogFormOptions':
    'Could not load the catalog form list. Please try again, or contact support if it continues.',
  'errors.failedToCreateCatalog': 'Failed to create catalog. Please try again.',
  'errors.failedToUpdateCatalog': 'Failed to update catalog. Please try again.',
  'errors.failedToListCatalog': 'Failed to list catalog. Please try again.',
  'errors.failedToFetchPartnerCatalog': 'Failed to fetch partner catalog. Please try again.',
  'errors.failedToUpdatePartnerCatalog': 'Failed to update partner catalog. Please try again.',
  'errors.failedToValidateDrugAvailability':
    'Failed to validate drug availability. Please try again.',

  'errors.otcCatalogNotFound': 'OTC catalog not found. Please verify the catalog information.',
  'errors.otcCatalogInactive': 'OTC catalog is inactive.',
  'errors.errorCreatingPomOtcOrder': 'Failed to create order. Please try again.',

  'errors.drugProfileNotFound': 'Drug profile not found. Please verify the drug information.',
  'errors.duplicateDrugProfile':
    'Drug profile already exists. Duplicate creation is not permitted.',
  'errors.drugCodeAlreadyExistInCatalog':
    'This drug code is already used by a different drug. Please choose a different drug code.',
  // Fallbacks only: these three carry backend messages that name the conflicting
  // drug and code, and getGraphQLErrorMessage prefers that message. They matter
  // on the getErrorMessage path, which has nothing but the i18nCode to go on.
  'errors.drugCodeMismatchForDrugName':
    'This drug already has a different drug code assigned. Reopen the dialog to pick up its current code.',
  'errors.drugCodeAlreadyMapped':
    'This drug code is already assigned to a different drug. Please choose a different drug code.',
  'errors.drugCodeMappingConflict':
    'That drug code was just claimed by another user. Please try again.',
  'errors.failedToResolveDrugProfile': 'Failed to resolve drug profile. Please try again.',
  'errors.failedToRetrieveDrugProfilesByNdc':
    'Failed to retrieve drug profiles by NDC. Please try again.',

  'errors.failedToListSigs': 'Failed to list SIGs. Please try again.',
  'errors.failedToFetchSigs': 'Failed to fetch SIGs. Please try again.',
  'errors.sigTextNotFoundError': 'SIG text not found. Please verify the SIG information.',
  'errors.sigLengthIsTooShort': 'SIG length is too short. Please provide a longer SIG.',
  'errors.sigAlreadyExists': 'SIG already exists.',
  'errors.failedToCreatingTheSigAssociation': 'Failed to create SIG association. Please try again.',
  'errors.failedToUpdatingTheSigAssociation': 'Failed to update SIG association. Please try again.',
  'errors.failedToFetchListOfSigAssociations':
    'Failed to fetch list of SIG associations. Please try again.',
  'errors.failedToGetTranslatedSig': 'Failed to get translated SIG. Please try again.',

  // Sig Management's own errors. Every one of these could be thrown before and none was mapped, so
  // the screen showed "Request failed. Please try again." for a sig that was simply too long or a
  // direction that already belonged to another sig — nothing the reviewer could act on.
  //
  // These are the fallback, not the first choice: the write paths show the backend's own message
  // via getGraphQLErrorMessage, which is more specific because it names the offending sig or the
  // limit that was exceeded. These cover the read paths and anything that only has the code.
  'errors.sigLengthIsTooLong': 'This SIG is longer than the maximum allowed. Please shorten it.',
  'errors.directionLengthIsTooShort':
    'This direction is too short. Please provide a longer direction.',
  'errors.directionLengthIsTooLong':
    'This direction is longer than the maximum allowed. Please shorten it.',
  // A direction belongs to exactly one SIG, so this is a fact to act on rather than a failure to
  // retry — the message says what to do. Sig Management names the SIG as well, from the extensions;
  // this is the fallback for anywhere that only has the code.
  'errors.directionAlreadyMapped':
    'This direction is already approved with a different SIG. A direction can belong to only one SIG, so set it inactive on that SIG first, then add it here.',
  'errors.sigIsRetired': 'This SIG is inactive. Reactivate it before changing its associations.',
  'errors.failedToFetchDirectionsForSig':
    'Failed to fetch the directions for this SIG. Please try again.',
  'errors.failedToFetchApprovedDrugsForSig':
    'Failed to fetch the catalog items for this SIG. Please try again.',
  'errors.failedToChangeSigCatalogApprovals':
    'The SIG was saved but its catalog approvals could not be changed. Please reopen the SIG and try again.',
  'errors.failedToFetchSigsForCatalogItem':
    'Failed to fetch the SIGs for this catalog item. Please try again.',
  'errors.failedToFindSigByDirectionText':
    'Failed to look up a SIG for this direction. Please try again.',

  'errors.qs1PatientNotFound': 'QS1 patient not found. Please verify the patient information.',
  'errors.qs1PrescriptionAssertionFail':
    'QS1 prescription assertion failed. Please verify the prescription.',
  'errors.qs1RxProfileNotExist': 'QS1 RX profile does not exist. Please verify the profile.',
  'errors.getQs1ProfileError': 'Failed to get QS1 profile. Please try again.',
  'errors.failedToFetchQs1PatientProfile': 'Failed to fetch QS1 patient profile. Please try again.',
  'errors.failedToSearchQs1PatientProfile':
    'Failed to search QS1 patient profile. Please try again.',
  'errors.qs1ValidationFailed': 'QS1 validation failed. Please check the data and try again.',
  'errors.multipleQs1ProfilesFound':
    'Multiple QS1 profiles found. Please specify which profile to use.',

  'errors.listPaymentTransactions': 'Failed to list payment transactions. Please try again.',
  'errors.getTransactionHistory': 'Failed to get transaction history. Please try again.',
  'errors.paymentHasBeenCapturedAlready': 'Payment has already been captured.',
  'errors.captureAmountExceedsAuthorizedAmount': 'Capture amount exceeds authorized amount.',
  'errors.productPricingDetailsNotFound': 'Product pricing details not found.',
  'errors.discountCardNotFoundError': 'Discount card not found. Please verify the discount card.',
  'errors.failedToFetchPatientRetryPayment':
    'Failed to fetch patient retry payment. Please try again.',

  'errors.refillAdjudicationNotFound':
    'Refill adjudication not found. Please verify the adjudication information.',
  'errors.failedToAdjudicateRefill': 'Failed to adjudicate refill. Please try again.',
  'errors.failedToFetchPatientUnapprovedRefills':
    'Failed to fetch patient unapproved refills. Please try again.',
  'errors.failedToCreateRefillOrder': 'Failed to create refill order. Please try again.',
  'errors.failedToSaveRefillRequest': 'Failed to save refill request. Please try again.',
  'errors.failedToSaveRefillRequestLineItem':
    'Failed to save refill request line item. Please try again.',
  'errors.failedToSaveRefillOrder': 'Failed to save refill order. Please try again.',
  'errors.failedToUpdateRefillRequestLineItem':
    'Failed to update refill request line item. Please try again.',

  'errors.addPatientMedicalHistoryError':
    'Failed to add patient medical history. Please try again.',
  'errors.getPatientMedicalHistoryError':
    'Failed to get patient medical history. Please try again.',
  'errors.listKnownMedicalConditionError':
    'Failed to list known medical conditions. Please try again.',

  'errors.noTestProfileAvailable': 'No test profile available. Please contact support.',
  'errors.activeTestClaimFound':
    'Active test claim found. Please resolve the existing test claim first.',
  'errors.activeTestClaimNotFound': 'Active test claim not found.',
  'errors.saveTestClaimFailed': 'Failed to save test claim. Please try again.',
  'errors.listTestClaimResultsError': 'Failed to list test claim results. Please try again.',
  'errors.failureReasonNotFound': 'Failure reason not found.',
  'errors.listPrescreeningResultsError': 'Failed to list prescreening results. Please try again.',

  'errors.failedToSavePatientNotificationPreferences':
    'Failed to save patient notification preferences. Please try again.',
  'errors.failedToSetPatientNotificationPreference':
    'Failed to set patient notification preference. Please try again.',
  'errors.failedToGetPatientNotificationPreference':
    'Failed to get patient notification preference. Please try again.',
  'errors.phonePreferenceNotAllowed': 'Phone preference is not allowed.',
  'errors.failedToSendOrderUpdateNotification':
    'Failed to send order update notification. Please try again.',
  'errors.failedToFetchOrderTrackingEmailTemplate':
    'Failed to fetch order tracking email template. Please try again.',
  'errors.failedToSendOrderUpdateEmail': 'Failed to send order update email. Please try again.',

  'errors.failedToAddPatientInCache': 'Failed to add patient in cache. Please try again.',
  'errors.failedToAddPatientExternalIdInCache':
    'Failed to add patient external ID in cache. Please try again.',

  'errors.failedToGetSignedInUrl': 'Failed to get signed URL. Please try again.',
  'errors.failedToGetRxProofUploadUrl': 'Failed to get RX proof upload URL. Please try again.',
  'errors.failedToGetPrescriptionSignedInUrls':
    'Failed to get prescription signed URLs. Please try again.',
  'errors.errorGettingFileUrl': 'Failed to get file URL. Please try again.',
  'errors.errorGeneratingFileUrls': 'Failed to generate file URLs. Please try again.',
  'errors.noAttachmentsFoundForThePrescription': 'No attachments found for the prescription.',

  'errors.statusConfigNotFound': 'Status configuration not found. Please contact support.',
  'errors.invalidDateError': 'Invalid date error. Please check the date format.',
  'errors.genderError': 'Gender error. Please check the gender value.',
  'errors.invalidNpi': 'The provided NPI is invalid. Please check and enter a valid NPI.',

  'errors.postEventInEventBusFailed': 'Failed to post event in event bus. Please try again.',
  'errors.failedToSyncExternalInteractionHistory':
    'Failed to sync external interaction history. Please try again.',
  'errors.failedToUpdateExternalInteractionHistory':
    'Failed to update external interaction history. Please try again.',
  'errors.failedPrescriberClarificationNeededCheck':
    'Prescriber clarification needed check failed.',
  'errors.prescriberClarificationNeeded':
    'Prescriber clarification is needed. Please contact the prescriber for more information.',
  'errors.controlledSubstanceCheckError':
    'Controlled substance check error. Please verify the substance information.',
  'errors.profitabilityResultForRequestLineItemError':
    'Failed to get profitability result for request line item.',
  'errors.copayResultCheckError': 'Copay result check error. Please try again.',
  'errors.failedToSyncPatientStatusHistoryError':
    'Failed to sync patient status history. Please try again.',
  'errors.syncPatientStatusHistoryError':
    'Failed to sync patient status history. Please try again.',
  'errors.failedToSyncPatientInsurancePlanTypeError':
    'Failed to sync patient insurance plan type. Please try again.',
  'errors.syncPatientInsurancePlanTypeError':
    'Failed to sync patient insurance plan type. Please try again.',
  'errors.updatePatientLastUsedInsuranceError':
    'Failed to update patient last used insurance. Please try again.',
  'errors.failedToCreateZendeskTicket': 'Failed to create Zendesk ticket. Please try again.',
  'errors.errorCreatingPrescriptionOrderInZendesk':
    'Failed to create prescription order in Zendesk. Please try again.',

  'errors.errorGettingPomRequest': 'Failed to get request. Please try again.',
  'errors.errorListingPomRequests': 'Failed to list requests. Please try again.',
  'errors.failedToCreatePomManualOrder': 'Failed to create manual order. Please try again.',
  'errors.failedToCreateReshipmentOrder': 'Failed to create reshipment order. Please try again.',

  'errors.createEVisitRequest': 'Failed to create e-visit request. Please try again.',
  'errors.failedToConstructEVisitRequest': 'Failed to construct e-visit request. Please try again.',
  'errors.failedToFetchAwaitingEVisitRequest':
    'Failed to fetch awaiting e-visit request. Please try again.',

  'errors.listPatientError': 'Failed to list patients. Please try again.',
  'errors.failedToGetPatientBalance': 'Failed to get patient balance. Please try again.',
  'errors.getPatientBalanceError': 'Failed to get patient balance. Please try again.',
  'errors.patientBalanceError': 'An error occurred with patient balance. Please try again.',
  'errors.patientContactNotFound': 'Patient contact not found.',
  'errors.failedToFetchOrdersForProductVerification':
    'Failed to fetch orders for product verification. Please try again.',
  'errors.productFulfillmentError': 'Product fulfillment error. Please try again.',
  'errors.bulkProductFill': 'Bulk product fill error. Please try again.',
  'errors.failedToBulkFillProduct': 'Failed to bulk fill product. Please try again.',
  'errors.failedToResolveMultiRxReview': 'Failed to resolve multi-RX review. Please try again.',
  'errors.failedToSendOrderBackForVerification':
    'Failed to send order back for verification. Please try again.',
  'errors.failedToFetchPatientOrderStatusHistory':
    'Failed to fetch patient order status history. Please try again.',
  'errors.campaignBundleNotFoundError': 'Campaign bundle not found.',
  'errors.failedToInitializeDbConnections':
    'Failed to initialize database connections. Please contact support.',
  'errors.failedToInitializeRepositories':
    'Failed to initialize repositories. Please contact support.',
  'errors.dbQueryTimeout':
    'Request timed out. Please try again. If the problem persists, contact support.',
  'errors.unexpectedUniqueConstraintViolation':
    'Unexpected unique constraint violation. Please contact support.',
  'errors.completeEntryFailed': 'Failed to complete entry. Please try again.',
  'errors.associatePatientWithExternalProfile':
    'Failed to associate patient with external profile. Please try again.',
  'errors.noOriginRequestFoundForThePatient': 'No origin request found for the patient.',
  'errors.failedToUpdateFillHistory': 'Failed to update fill history. Please try again.',
  'errors.failedToFetchPatientHistoryTrackList':
    'Failed to fetch patient history track list. Please try again.',
  'errors.failedToListRxIntakeRequests': 'Failed to list RX intake requests. Please try again.',
  'errors.failedToListExternalSystemOrders':
    'Failed to list external system orders. Please try again.',
  'errors.errorResolvingOnHoldRequest': 'Failed to resolve on-hold request. Please try again.',
  'errors.errorDeletingOnHoldRequestPrescriptions':
    'Failed to delete on-hold request prescriptions. Please try again.',
  'errors.failedToAuditViewPrescription': 'Failed to audit view prescription. Please try again.',
  'errors.failedToListPrescriptions': 'Failed to list prescriptions. Please try again.',
  'errors.failedToListEScriptPrescriptions':
    'Failed to list e-script prescriptions. Please try again.',
  'errors.failedToCancelEscribeOrder': 'Failed to cancel e-scribe order. Please try again.',
  'errors.failedToVerifyToken': 'Failed to verify token. Please try again.',
  'errors.resolverTimeout':
    'Request timed out. Please try again. If the problem persists, contact support.',
  'errors.pharmacyWithSameDetailsAlreadyExists':
    'A pharmacy with the same name or NPI already exists. Please use different information or contact support.',
  'errors.orderNotEligibleForTransfer':
    'This order is not eligible for transfer. Please verify the order status and try again.',
  'errors.transferPharmacyNotFound':
    'Transfer pharmacy not found. Please verify the pharmacy information.',
  'errors.transferPharmacyFaxNotFound':
    'Transfer pharmacy fax number not found. Please verify the pharmacy fax information.',
  'errors.transferPdfGenerationFailed':
    'Failed to generate transfer PDF. Please try again or contact support.',
  'errors.transferPdfS3UploadFailed':
    'Failed to upload transfer PDF to storage. Please try again or contact support.',
  'errors.transferFaxSendFailed':
    'Failed to send transfer fax. Please try again or contact support.',
  'errors.transferFaxSizeExceeded':
    'Transfer fax size exceeds the maximum allowed limit. Please contact support.',
  'errors.transferOrderFailed': 'Failed to transfer order. Please try again or contact support.',
  'errors.failedToTransferOrdersToExternalPharmacy':
    'Failed to transfer orders to external pharmacy. Please try again or contact support.',
  'errors.editOrderShipmentFailed': 'Failed to mark order as shipped.',

  // Inventory management (see smartphos-core errorEnum.ts).
  // errors.itemLockedForPhysicalCount and errors.alreadyScanned are defined above.
  'errors.failedToListInventoryItems':
    'Failed to load inventory items. Please try again or contact support.',
  'errors.failedToListLockedInventoryItems':
    'Failed to load locked inventory items. Please try again or contact support.',
  'errors.failedToListInventoryByNdc':
    'Failed to load inventory for the selected NDC. Please try again or contact support.',
  'errors.failedToListInventoryTransactions':
    'Failed to load inventory transactions. Please try again or contact support.',
  'errors.failedToGetInventoryItem':
    'Failed to load the inventory item. Please try again or contact support.',
  'errors.inventoryItemNotFound': 'Inventory item not found. Please verify the item and try again.',
  'errors.ndcNotFoundInInventory':
    'The NDC entered was not found in inventory. Please verify the NDC and try again.',
  'errors.facilityInventoryItemNotFound':
    'Inventory item not found for this facility. Please verify the item and try again.',
  'errors.inventoryItemAlreadyExists':
    'An inventory item with these details already exists. Please use a different item or contact support.',
  'errors.failedToCreateInventory':
    'Failed to create the inventory item. Please check the details and try again.',
  'errors.inventoryModuleDisabled':
    'The inventory module is not enabled for this pharmacy. Please contact support to enable it.',

  'errors.invalidReorderConfig':
    'The reorder configuration is invalid. Please check the values and try again.',
  'errors.failedToUpdateInventoryReorderConfig':
    'Failed to update the reorder configuration. Please try again or contact support.',

  'errors.inventoryLotNotFound': 'Inventory lot not found. Please verify the lot and try again.',
  'errors.inventoryLotAlreadyExists':
    'An inventory lot with these details already exists. Please use a different lot or contact support.',
  'errors.failedToCreateInventoryLot':
    'Failed to create the inventory lot. Please check the details and try again.',
  'errors.failedToUpdateInventoryLot':
    'Failed to update the inventory lot. Please try again or contact support.',
  'errors.failedToListInStockLots':
    'Failed to load in-stock lots. Please try again or contact support.',
  'errors.failedToGetLotSummary':
    'Failed to load the lot summary. Please try again or contact support.',
  'errors.insufficientLotQuantity':
    'There is not enough quantity available in this lot to complete the action. Please verify the available quantity.',
  'errors.lotNotBelongsToItem':
    'The selected lot does not belong to this inventory item. Please verify the lot and try again.',

  'errors.failedToAllocateLots': 'Failed to allocate lots. Please try again or contact support.',
  'errors.failedToUnallocateLots':
    'Failed to unallocate lots. Please try again or contact support.',
  'errors.failedToResolveLotAllocations':
    'Failed to resolve lot allocations. Please try again or contact support.',
  'errors.overAllocation':
    'The requested quantity exceeds the available quantity. Please reduce the quantity and try again.',
  'errors.failedToGetDispenseCandidate':
    'Failed to determine a dispense candidate. Please try again or contact support.',
  'errors.allocatedLotExpired':
    'The lot allocated to this item has expired and cannot be dispensed. Please select a non-expired lot.',
  'errors.allocatedLotUnavailable':
    'The lot allocated to this item is no longer available. Please select a different lot.',
  // POM-6083 — manual lot entry at fulfilment. Kept distinct from
  // allocatedLotExpired on purpose: a lot created from a fulfilment scan has no
  // BUD yet, so nothing has expired and telling the user it has sends them
  // looking for the wrong problem.
  'errors.allocatedLotBudMissing':
    'The lot allocated to this item has no expiration date yet. Add the expiration date to the lot in Inventory, or select a different lot.',
  'errors.inventoryNdcRequired':
    'An NDC is required to record this lot. Scan or select the product, then try again.',
  // POM-6083 - hand-recording is only for stock inventory has no quantity for. A lot that
  // still has quantity has to go through the dispense picker, which applies FEFO order and
  // the expiry check that recording by hand bypasses.
  // POM-6083 — a lot's BUD is part of its identity, so correcting one is an inventory
  // change with a reason and an approver, not something a fill may do. Also reached in a
  // race: two operators pick the same BUD-less lot and the second one loses.
  'errors.inventoryLotBudAlreadySet':
    'This lot already has an expiration date, or another user has just set one. Reopen the fill to see the current lot details.',
  'errors.inventoryLotBudInvalid':
    'That expiration date is not valid. Enter it as a real calendar date from the lot label.',
  'errors.inventoryUnverifiedOverResolve': `This lot no longer carries that much ${SYSTEM_DEFICIT.toLowerCase()} quantity — another user may have resolved it. Reopen the review to see the current amounts.`,
  // POM-6111 - a receipt that settles part of a lot's unverified balance. Every guard on that
  // path reached the user as the generic default, because ErrorDialog resolves through
  // getErrorMessage, which genericises ANY i18nCode it has no entry for - so core's own
  // messages, which name the lot and the balances, never arrived.
  //
  // Both entry points share these: New Stock Receipt and Add Items send the same
  // `unverifiedTally`.
  'errors.inventoryTallyBudRequired': `A BUD is required to settle a lot's ${SYSTEM_DEFICIT.toLowerCase()} quantity. The expiration date is part of the lot's identity, so without it the receipt cannot be matched to the lot it belongs to.`,
  'errors.inventoryUnverifiedReasonRequired': `A reason is required to settle a lot's ${SYSTEM_DEFICIT.toLowerCase()} quantity. Choose one and try again.`,
  // The lot named by the tally is not this receipt's: a different drug, a different facility, or
  // one that no longer carries a balance at all. Usually means the lot details were read before
  // someone else resolved them.
  'errors.inventoryTallyLotMismatch': `That lot does not match this receipt, or no longer carries a ${SYSTEM_DEFICIT.toLowerCase()} quantity. Reopen the receipt to see the current lot details.`,
  // A dated lot's BUD is fixed: correcting one is an inventory change with its own reason and
  // approver, not something a receipt may do on the way past.
  'errors.inventoryTallyBudConflict':
    "The BUD on this line does not match the existing lot. Correct the receipt to the lot's own expiration date, or adjust the lot separately in Inventory.",
  // Two rows for one physical lot. Merging them is a stock decision with its own audit trail, so
  // neither the receipt nor this dialog can do it.
  'errors.inventoryTallyLotMergeRequired': `This lot exists twice for the same product and expiration date. The two records refer to the same physical lot and must be merged before the ${SYSTEM_DEFICIT.toLowerCase()} quantity can be settled.`,
  'errors.inventoryTallyInvalidQuantity': `Enter a quantity of at least 1 to settle against this lot, or 0 to leave the ${SYSTEM_DEFICIT.toLowerCase()} quantity open.`,
  // Bounded by BOTH balances: the claim, and what this receipt actually brought in. Core names
  // which bound was crossed in the message detail.
  'errors.inventoryTallyExceedsBalance': `That is more than this lot can account for — a settlement cannot exceed either the ${SYSTEM_DEFICIT.toLowerCase()} quantity or what this receipt received. Lower it and try again.`,
  'errors.inventoryLotHasAvailableStock':
    'This lot still has quantity in inventory. Select it from the available lots instead of recording it by hand.',
  'errors.inventoryLotBudAmbiguous':
    'This lot number already exists more than once for this product with different expiration dates. Enter the expiration date to say which one you have.',
  // A manufacturer lot number identifies one product, so the same number under a second NDC
  // means the lot was typed against the wrong drug. Core names the drug that owns it in the
  // message detail; this is the instruction that goes with it.
  'errors.inventoryLotBelongsToAnotherDrug':
    'This lot number already belongs to a different drug. A lot number belongs to one product, so either the drug or the lot number is wrong — check the vial and re-enter it.',
  // POM-6086 — the repeat itself is allowed; what is missing is the reason for it. Both
  // receiving dialogs now ask inline, so this only surfaces when a request arrives without one:
  // a stale tab, or a claimed lot whose tally was zeroed after the picker had been answered.
  // The three options are named because the operator has to recognise theirs to pick it.
  'errors.inventoryExistingLotReasonRequired':
    'This lot number is already in inventory. Recording it again is allowed — say why it is here (a new shipment of the same lot, an earlier arrival that was never recorded, or a transfer from another facility) and submit again.',
  'errors.failedToListAvailableLotsForDispense':
    'Failed to load available lots for dispensing. Please try again or contact support.',

  'errors.failedToListLowStockItems':
    'Failed to load low-stock items. Please try again or contact support.',
  'errors.failedToListExpiringSoonItems':
    'Failed to load expiring-soon items. Please try again or contact support.',
  'errors.failedToGetExpiringSoonLots':
    'Failed to load expiring-soon lots. Please try again or contact support.',
  'errors.failedToListLowDaysOfSupplyItems':
    'Failed to load low days-of-supply items. Please try again or contact support.',
  'errors.failedToGetInventoryAlertSummary':
    'Failed to load the inventory alert summary. Please try again or contact support.',
  'errors.failedToResolveInventoryAlertNames':
    'Failed to resolve inventory alert names. Please try again or contact support.',

  'errors.failedToManualInventoryAdjust':
    'Failed to adjust inventory. Please check the details and try again.',
  'errors.manualAdjustInsufficientStock':
    'There is not enough stock available to make this adjustment. Please verify the available quantity.',

  'errors.failedToCreateInventoryReceiving':
    'Failed to create the inventory receiving. Please check the details and try again.',
  'errors.failedToUpsertInventoryReceiving':
    'Failed to save the inventory receiving. Please check the details and try again.',
  'errors.failedToGetInventoryReceivings':
    'Failed to load inventory receivings. Please try again or contact support.',
  'errors.inventoryReceivingNotFound':
    'Inventory receiving not found. Please verify the receiving and try again.',
  'errors.invalidUpsertInventoryReceivingInput':
    'The receiving details provided are invalid. Please check your input and try again.',
  'errors.receivingLineNotFound': 'Receiving line not found. Please verify the line and try again.',
  'errors.parentNotFound':
    'The parent record could not be found. Please verify your selection and try again.',
  'errors.parentNotEditable': 'This record can no longer be edited. Please refresh and try again.',
  'errors.duplicatePharmacyInRequest':
    'The same pharmacy appears more than once in the request. Please remove the duplicate and try again.',
  'errors.duplicatePharmacyExists':
    'This pharmacy has already been added. Please use a different pharmacy.',

  'errors.failedToCreateInventoryReceivingLineSplits':
    'Failed to create receiving line splits. Please check the details and try again.',
  'errors.failedToGetInventoryReceivingLineSplits':
    'Failed to load receiving line splits. Please try again or contact support.',
  'errors.failedToVerifyInventoryReceivingLineSplit':
    'Failed to verify the receiving line split. Please try again or contact support.',
  'errors.failedToUpdateInventoryReceivingLineSplitQuantity':
    'Failed to update the receiving line split quantity. Please try again or contact support.',
  'errors.invalidSplitQuantity':
    'The split quantity is invalid. Please check the value and try again.',
  'errors.invalidSplitsListParams':
    'The parameters provided for listing splits are invalid. Please check your input and try again.',
  'errors.splitNotFound': 'Split not found. Please verify the split and try again.',
  'errors.splitAlreadyVerified': 'This split has already been verified and cannot be changed.',
  'errors.invalidOffageReasonQuantity':
    'The offage reason quantity is invalid. Please check the values and try again.',
  'errors.formularyMissingProductId':
    'This formulary item is missing a product ID. Please update the formulary and try again.',

  'errors.failedToListVendors': 'Failed to load vendors. Please try again or contact support.',
  'errors.failedToAddVendor': 'Failed to add the vendor. Please check the details and try again.',
  'errors.vendorAlreadyExists':
    'A vendor with these details already exists. Please use a different vendor.',

  'errors.alreadyInPhysicalCount':
    'This item is already in a physical count. Please complete or cancel the existing count first.',
  'errors.itemNotInPhysicalCount':
    'This item is not part of the physical count. Please verify and try again.',
  'errors.inventoryCountNotFound':
    'Physical count not found. Please verify the count and try again.',
  'errors.invalidCountedQty':
    'The counted quantity is invalid. Please enter a valid quantity and try again.',
  'errors.failedToLockPhysicalCount':
    'Failed to lock the physical count. Please try again or contact support.',
  'errors.failedToUnlockPhysicalCount':
    'Failed to unlock the physical count. Please try again or contact support.',
  'errors.failedToCreateCount':
    'Failed to create the physical count. Please try again or contact support.',
  'errors.failedToListCounts':
    'Failed to load physical counts. Please try again or contact support.',
  'errors.lotNotFoundForCount':
    'Lot not found for this count. Please verify the lot and try again.',
  'errors.notesRequiredForCountIncrease':
    'Notes are required when increasing the counted quantity. Please add a note and try again.',
  'errors.offageRequiredForCountDecrease':
    'An offage reason is required when decreasing the counted quantity. Please provide one and try again.',
  // The reason picked to clear a lot's unverified quantity during a count cannot be applied: a
  // "stock was real" reason with no surplus counted, or the balance was cleared by someone else
  // since the count was opened. The form prevents the first, so this is usually the second.
  'errors.countUnverifiedClearInvalid': `The reason chosen cannot clear this lot's ${SYSTEM_DEFICIT.toLowerCase()} quantity. A reason that says the stock was real needs a counted surplus. Reopen the count to see the lot's current balance and try again.`,

  'errors.failedToListReversalReviewQueue':
    'Failed to load the reversal review queue. Please try again or contact support.',

  'errors.concurrentPatientCreation':
    'This patient is already being added by another request. Refresh and check whether the patient now exists before trying again.',
  'errors.failedToUpdateUser': 'Failed to update the user. Please try again or contact support.',
  'errors.failedToGetUsers': 'Failed to load the users. Please try again or contact support.',
  'errors.failedToAddExternalTransferPharmacy':
    'Failed to add the external transfer pharmacy. Please check the details and try again.',
  'errors.failedToEditExternalTransferPharmacy':
    'Failed to update the external transfer pharmacy. Please check the details and try again.',
  'errors.failedToDeleteExternalTransferPharmacy':
    'Failed to delete the external transfer pharmacy. Please try again or contact support.',
  'errors.externalTransferPharmacyNotFound':
    'This external transfer pharmacy could not be found. Refresh the page and try again.',
  'errors.transferDestinationNotEligible':
    'The selected facility is not eligible to receive transferred orders. Please choose a different facility.',
  'errors.failedToFetchTransferOrderDetails':
    'Failed to load the transfer order details. Please try again or contact support.',
  'errors.failedToListExternalPharmacies':
    'Failed to load the pharmacy list. Please try again or contact support.',
  'errors.failedToFetchOriginalOrderPayload':
    'The original order request could not be loaded. Please contact support.',
  'errors.apiRequestsTableNotConfigured':
    'Order request history is not available in this environment. Please contact support.',
  'errors.failedToImportEscriptOrder':
    'Failed to import the e-script order. Please try again or contact support.',
  'errors.errorGettingEScriptOrderByReferenceId':
    'Failed to load the e-script order for this reference. Please try again or contact support.',
  'errors.patientExternalIdNotFoundForPartner':
    'The external patient ID does not match any patient for this partner. Please verify the ID and try again.',
  'errors.failedToAssociateCatalogProduct':
    'Failed to link the catalog product. Please try again or contact support.',
  'errors.failedToResolveCatalogProduct':
    'The catalog product for this item could not be found. Please verify the product and try again.',
  'errors.productIdAlreadyExistInCatalog':
    'This product ID already exists in the catalog. Please use a different product ID.',
  'errors.invalidDrugCode':
    'The drug code must be exactly 3 letters. Please correct it and try again.',
  'errors.failedToUpdatePartnerCompoundingConfig':
    "Failed to update the partner's compounding settings. Please try again or contact support.",
  'errors.failedToUpdatePartnerCarrierTrackingWebhookConfig':
    "Failed to update the partner's carrier tracking webhook settings. Please try again or contact support.",
  'errors.failedToGetPartnerWebhookSettings':
    "Failed to load the partner's webhook settings. Please try again or contact support.",
  'errors.failedToUpdatePartnerWebhookSettings':
    "Failed to update the partner's webhook settings. Check that the webhook URL and contact email are valid and try again.",
  'errors.failedToUpdatePartnerPracticeSmsConfig':
    "Failed to update the partner's practice SMS settings. Please try again or contact support.",
  'errors.failedToUpdatePartnerShippingOptions':
    "Failed to update the partner's shipping options. Please try again or contact support.",
  'errors.failedToUpdatePracticeShippingOptions':
    "Failed to update the practice's shipping options. Please try again or contact support.",
  'errors.partnerShippingOptionsNotConfigured':
    'Shipping options are not configured for this partner. Please set them up before continuing.',
  'errors.webhookResendEventNotFound':
    'The original webhook event could not be found, so it cannot be resent.',
  'errors.webhookResendPayloadUnavailable':
    'The original webhook content is not available for this event, so it cannot be resent.',
  'errors.webhookResendPartnerInactive':
    "The partner's webhook is inactive, so the event cannot be resent. Activate the webhook and try again.",
  'errors.webhookResendFailed':
    'Failed to resend the webhook. Please try again or contact support.',
  'errors.failedToGetPrescriptionByNdc':
    'Failed to load fill history for this NDC. Please try again or contact support.',
  'errors.failedToListFillHistoryUpdateReports':
    'Failed to load the fill history update reports. Please try again or contact support.',
  'errors.failedToGetOrderShipment':
    "Failed to load the order's shipments. Please try again or contact support.",
  'errors.cacheUnavailable':
    'A temporary system issue prevented this request from completing. Please try again in a few minutes. If the problem continues, contact support.',
  'errors.failedToGenerateLabelForShipment':
    'Failed to generate the shipping label. Please try again or contact support.',
  'errors.failedToDeleteAddressOrderLineItem':
    'Failed to remove the item from this shipping address. Please try again or contact support.',
  'errors.failedToMarkShipmentAsShipped':
    'Failed to mark the shipment as shipped. Please try again or contact support.',
  'errors.failedToUpdateShipment':
    'Failed to update the shipment. Please try again or contact support.',
  'errors.failedToCancelPrxShipment':
    'Failed to cancel the shipment. Please try again or contact support.',
  'errors.prxShipmentError':
    'The shipping service could not complete this request. Please try again or contact support.',
  'errors.prxShipmentTimeout':
    'The shipping service took too long to respond. Please check whether the shipment was created before trying again.',
  'errors.resolveShipmentMpiContextError':
    'Failed to load the shipment details for this order. Please try again or contact support.',
  'errors.failedToListCentralFillOrders':
    'Failed to load central fill orders. Please try again or contact support.',
  'errors.failedToSendOrdersToCentralFill':
    'Failed to send the orders to central fill. Please try again or contact support.',
  'errors.failedToResolveCentralFillReview':
    'Failed to resolve the central fill review. Check the selected state and try again. If the problem continues, contact support.',
  'errors.compoundingPharmacyNotFound':
    'The selected compounding pharmacy could not be found. Refresh the page and try again.',
  'errors.failedToRecallCentralFillOrders':
    'Failed to recall the central fill orders. Please try again or contact support.',
  'errors.failedToForceReturnCentralFillOrders':
    'Failed to force return the compounding orders. Please try again or contact support.',
  'errors.failedToUpdateCentralFillOrderStatus':
    'Failed to update the central fill order status. Please try again or contact support.',
  'errors.centralFillOrderNotEligibleForStatusUpdate':
    'This central fill order cannot be moved to the requested status from its current status. Refresh to see its current state.',
  'errors.failedToBulkVerifyOrders':
    'Failed to verify the selected orders. Please try again or contact support.',
  'errors.splitNotCallersFacility':
    'This distribution can only be verified by a user with access to the destination facility.',
  'errors.failedToListProductNdcs':
    'Failed to load the NDCs for this product. Please try again or contact support.',
  'errors.failedToListCatalogNdcsByProductId':
    'Failed to load the catalog NDCs for this product. Please try again or contact support.',
  'errors.failedToListInventoryManufacturers':
    'Failed to load the manufacturer list. Please try again or contact support.',
  // POM-6405 — Inventory → Manufacturer Management.
  'errors.failedToListManufacturers':
    'Failed to load manufacturers. Please try again or contact support.',
  'errors.failedToAddManufacturer':
    'Failed to add the manufacturer. Please try again or contact support.',
  'errors.failedToUpdateManufacturer':
    'Failed to rename the manufacturer. Please try again or contact support.',
  'errors.failedToDeleteManufacturer':
    'Failed to remove the manufacturer. Please try again or contact support.',
  'errors.manufacturerAlreadyExists': 'A manufacturer with this name already exists.',
  'errors.manufacturerNotFound':
    'This manufacturer no longer exists or has already been removed. Refresh the list and try again.',
  'errors.invalidManufacturerName': 'Enter a manufacturer name of 255 characters or fewer.',
  'errors.inventoryNdcNotFoundForProduct':
    'This NDC is not an inventory record for the selected product. Please choose a different NDC.',
  'errors.inventoryNdcInactiveForDefault':
    'This NDC is not active for the selected product and cannot be set as the default.',
  'errors.failedToSetDefaultNdcForProduct':
    'Failed to set the default NDC for this product. Please try again or contact support.',
  'errors.partnerNdcPreferenceInvalidNdc':
    'This NDC is not an active inventory record for the selected product. Please choose a different NDC.',
  'errors.failedToSetPartnerNdcPreference':
    'Failed to set the partner NDC preference. Please try again or contact support.',
  'errors.failedToRemovePartnerNdcPreference':
    'Failed to remove the partner NDC preference. Please try again or contact support.',
  'errors.failedToListPartnerNdcPreferences':
    'Failed to load the partner NDC preferences. Please try again or contact support.',
  'errors.failedToListPartnerNdcPreferenceCounts':
    'Failed to load the partner NDC preference counts. Please try again or contact support.',
  'errors.failedToGetInventoryLotsByLotId':
    'Failed to load the lot balances. Please try again or contact support.',
  'errors.failedToListRecordableLotsForProduct':
    'Failed to load the lots for this product. Please try again or contact support.',
  'errors.failedToGetActiveAllocationsForLineItem':
    'Failed to load the allocations for this item. Please try again or contact support.',
  'errors.failedToDiscardAllocationOfLineItems':
    'Failed to release the allocations for this item. Please try again or contact support.',
  'errors.failedToRecallLotAllocations':
    'Failed to recall the lot allocations to inventory. Please try again or contact support.',
  'errors.lotRecallAlreadyInProgress':
    'A recall is already in progress for this drug at one of the selected locations. Wait for it to finish and try again.',
  'errors.lotRecallLockUnavailable':
    'We could not confirm that no other recall is running. Please try again.',
  'errors.lineItemNotInCallersFulfillingPharmacy':
    'This item belongs to a pharmacy you do not have access to.',
  'errors.facilityNotInCallersAccess': 'You do not have access to this facility.',
  'errors.noCallerFulfillingPharmacies':
    'Your account does not have access to any fulfilling pharmacy. Please contact support.',
  'errors.rxAlreadyScanned': 'This Rx has already been scanned.',
  'errors.scannedNdcMismatch':
    'The scanned NDC does not match the NDC assigned to this Rx. Please scan the correct product.',
  'errors.assignedNdcNotFound':
    'This Rx has no assigned NDC, so the scanned NDC cannot be checked. Please contact support.',
  'errors.lotNdcMismatch':
    'The selected lot does not belong to this NDC. Please choose a lot for the scanned NDC.',
  'errors.overrideNdcNotInInventory':
    'The scanned NDC is not stocked at this pharmacy, so it cannot be filled or allocated.',
  'errors.overrideNdcProductMismatch':
    'The scanned NDC belongs to a different product than the one assigned to this Rx. Please scan the correct product.',
  'errors.overrideProductNdcNotInInventory':
    'The selected NDC is not stocked at this pharmacy for this product. Please choose a different NDC.',
  'errors.lineItemAlreadyTaggedForReview': 'This item is already marked for review.',
  'errors.unsupportedReviewTarget':
    'This review type is not supported for bulk review. Please choose a different review type.',
  'errors.lineItemNotEligibleForOverride':
    'This item is not eligible for this change in its current state. Refresh to see its current state.',
  'errors.lineItemDuplicatePrescriptionInBatch':
    'The same prescription appears more than once in this selection. Remove the duplicate and try again.',
  'errors.failedToListOpenLineItemsForProductOverride':
    'Failed to load the open items for this product. Please try again or contact support.',
  'errors.failedToBulkOverrideLineItems':
    'Failed to apply the bulk update. Please try a smaller selection, or contact support if the problem continues.',
  'errors.failedToLoadOrderRejectionConfig':
    'Failed to load the order acceptance rules. Please try again or contact support.',
  'errors.failedToListInvoiceGroups':
    'Failed to load the invoice groups. Please try again or contact support.',
  'errors.failedToListInvoices':
    'Failed to load the invoices. Please try again or contact support.',
  'errors.failedToGetInvoiceDetails':
    'Failed to load the invoice details. Please try again or contact support.',
  'errors.invalidInvoiceId': 'This invoice ID is not valid. Please check it and try again.',
  'errors.invoiceNotFound': 'This invoice could not be found. Refresh the page and try again.',
  'errors.invoiceAlreadyPaid':
    'This invoice has already been paid and cannot be changed or voided.',
  'errors.failedToVoidInvoice': 'Failed to void the invoice. Please try again or contact support.',
  'errors.invoiceCannotBeMarkedPaid':
    'This invoice is already paid or void and cannot be marked paid. Refresh the page to see its current status.',
  'errors.failedToMarkInvoicePaid':
    'Failed to mark the invoice paid. Please try again or contact support.',
  'errors.failedToEditInvoice':
    'Failed to update the invoice. Please try again or contact support.',
  'errors.invoicePeriodAlreadyExists':
    'An invoice already exists for this group and billing period.',
  'errors.invoiceGroupNotFound':
    'This invoice group could not be found. Refresh the page and try again.',
  'errors.failedToCreateInvoice':
    'Failed to create the invoice. Please try again or contact support.',
  'errors.failedToListProductPricings':
    'Failed to load the product pricing. Please try again or contact support.',
  'errors.failedToUpdateProductPricings':
    'Failed to save the product pricing. Please try again or contact support.',
  'errors.coaBucketNotConfigured':
    'COA documents are not available in this environment. Please contact support.',
  'errors.invalidCoaInput':
    'Some of the COA details are not valid. Please check them and try again.',
  'errors.coaLotNotInInventory':
    'No inventory lot matches this NDC and lot number. Please check them and try again.',
  'errors.failedToListCoaDocuments':
    'Failed to load the COA documents. Please try again or contact support.',
  'errors.failedToListCoaEligibleLots':
    'Failed to load the lots that can have a COA. Please try again or contact support.',
  'errors.failedToCreateCoaUploadUrl':
    'Failed to prepare the COA upload. Please try again or contact support.',
  'errors.failedToGetCoaDocument':
    'Failed to load the COA document. Please try again or contact support.',
  'errors.failedToGetOrderCoaDocuments':
    'Failed to load the COA documents for this order. Please try again or contact support.',
  'errors.coaRateLimited': 'Too many lookups. Please wait a minute and try again.',
  'errors.patientLookupTokenStoreUnavailable':
    'Patient lookup is temporarily unavailable. Please try again in a few minutes.',
  'errors.patientLookupFailed': 'Unable to complete the lookup. Please try again.',
  'errors.patientOrderStatusFailed': 'Unable to retrieve the order status. Please try again.',
  'errors.patientPushHandleStoreUnavailable':
    'Notifications are temporarily unavailable. Please try again in a few minutes.',
  'errors.patientDeviceRegisterFailed':
    'Unable to register the device for notifications. Please try again.',
  'errors.patientOrderSubscribeFailed': 'Unable to subscribe to order updates. Please try again.',
  'errors.patientDeviceDeregisterFailed':
    'Unable to remove the device from notifications. Please try again.',
  'errors.relayOrderReferenceInvalid': 'The relay order reference is not valid.',
  'errors.relayOrderNotRelayRouted':
    'This order is not set up to be relayed to a partner pharmacy.',
  'errors.relayOrderUnsupportedLineItemCount': 'A relay order must have exactly one item.',
  'errors.relayOrderLineItemInvalid':
    'The relay order item is not valid. Check the item quantity and try again.',
  'errors.relayOrderOrderInvalid': 'The relay order is not valid.',
  'errors.relayOrderReferenceReused':
    'This relay order reference has already been used by another order.',
  'errors.relayOrderIntakeFailed':
    'Failed to accept the relay order. Please try again or contact support.',
  'errors.relayOrderCancelNotSupported':
    'Relay orders cannot be cancelled this way. Please contact support.',
  'errors.relayOrderStatusUnmapped':
    'The relay order status cannot be reported to the partner. Please contact support.',
  'errors.relayQuelloAuthFailed':
    'Could not sign in to the partner pharmacy. Please contact support.',
  'errors.relayIntegrationMismatch':
    'This relay order is set up for a different partner pharmacy integration. Please contact support.',
  'errors.relayIntegrationNotConfigured':
    'The partner pharmacy integration is not set up. Please contact support.',
  'errors.relayOrderShipToUnavailable':
    'The relay order has no usable shipping address. Please check the address and try again.',
  'errors.relayOrderNotSubmittable':
    'The partner pharmacy did not accept the relay order. Please contact support.',
  'errors.relayOrderNoLineItem': 'The relay order has no item to submit.',
  'errors.relayShipToPracticeNotSupported':
    "Relay orders cannot be shipped to a practice. Please use the patient's address.",
  'errors.relayOrderPatientIncomplete':
    'The relay order is missing patient details the partner pharmacy needs.',
  'errors.relayOrderPrescriptionIncomplete':
    'The relay order prescription is missing details the partner pharmacy needs.',
  'errors.relayOrderReadFailed':
    'Failed to load the relay order details. Please try again or contact support.',
  'errors.relayOrderLookupInconclusive':
    'We could not confirm whether the partner pharmacy already has this order. Please check before trying again.',
  'errors.nonRoutableRequestReadFailed':
    'Failed to load the non-routable requests. Please try again or contact support.',
  'errors.nonRoutableRequestFilterInvalid':
    'One of the date filters is not valid. Please correct it and try again.',
  'errors.nonRoutableRequestIdInvalid': 'This non-routable request ID is not valid.',
  'errors.nonRoutableRequestNotFound':
    'This non-routable request could not be found. Refresh the page and try again.',
  'errors.nonRoutableRequestNotPending':
    'This non-routable request is no longer pending, so this action is not available. Refresh to see its current state.',
  'errors.nonRoutableRequestClaimLost':
    'Another user is already resolving this non-routable request. Refresh to see its current state.',
  'errors.nonRoutableRequestClaimFailed':
    'Could not claim the non-routable request. Its state is unknown and it may need manual review. Please contact support.',
  'errors.nonRoutableRequestMessageUnusable':
    'This request cannot be released or retried because its stored details are incomplete. Please contact support.',
  'errors.nonRoutableRequestReleasePublishFailed':
    'Failed to release the non-routable request. It is still parked and can be retried.',
  'errors.nonRoutableRequestReleaseRollbackFailed':
    'The release failed and the request could not be returned to the queue. Nothing was sent and no order was created. Please contact support.',
  'errors.nonRoutableRequestQueueUnavailable':
    'The request could not be released because the onboarding queue is not available. It is still parked. Please contact support.',
  'errors.nonRoutableRequestRoutingUnavailable':
    'Routing could not be re-run and the request could not be returned to the queue. Please contact support.',
  'errors.nonRoutableRequestRetryPublishFailed':
    'Failed to retry the non-routable request. It is still parked and can be retried.',
  'errors.nonRoutableRequestRetryRollbackFailed':
    'The retry failed and the request could not be returned to the queue. Nothing was retried and no order was created. Please contact support.',
  'errors.nonRoutableRequestRetryQueueUnavailable':
    'The request could not be retried because the routing queue is not available. It is still parked. Please contact support.',
  'errors.nonRoutableRequestRetryReadBackFailed':
    'The retry was sent and routing is running again. Re-open the record to see its current state.',
  'errors.nonRoutableRequestResolutionNotRecorded':
    'The request was released, but its record could not be updated. Do not release it again. Please contact support.',
  'errors.nonRoutableRequestReleasedReadBackFailed':
    'The request was released and recorded. Re-open the record to see its current state.',
  'errors.nonRoutableRequestCancelLost':
    'Someone else changed this non-routable request, so it was not cancelled. Refresh to see its current state.',
  'errors.nonRoutableRequestCancelFailed':
    'Could not cancel the non-routable request. Its state is unknown and it may need manual review. Please contact support.',
  'errors.nonRoutableRequestCancelledReadBackFailed':
    'The request was cancelled. Re-open the record to see its current state.',

  // Framework-level codes. These are not domain errors — they come from Apollo/graphql-js
  // or the gatekeeper's UNKNOWN_ERROR fallback, and their raw messages are written for
  // developers (schema field dumps, syntax errors, connection strings). See
  // FRAMEWORK_ERROR_CODES below.
  'errors.graphqlValidationFailed':
    'This page is out of date and sent a request the server did not recognize. Please refresh and try again. If the problem persists, contact support.',
  'errors.graphqlParseFailed':
    'The request could not be read by the server. Please refresh and try again. If the problem persists, contact support.',
  'errors.badUserInput':
    'Some of the values sent with this request were not valid. Please check your input and try again.',
  'errors.unauthenticated': 'Your session has expired. Please sign in again.',
  'errors.forbidden':
    'You are not authorized to perform this action. Please contact support if you believe this is an error.',
};

// Codes whose raw backend message must never reach the user. Everything here is
// framework- or transport-level: Apollo Server and graphql-js built-ins, plus the
// gatekeeper's NOT_AUTHORIZED / UNKNOWN_ERROR fallbacks (see
// smartscripts-maap-gatekeeper simpleErrorCustomizer, which passes the original
// message through untouched and camelCases the code into an i18nCode).
//
// This is deliberately a denylist, not an allowlist: smartphos-core's errorEnum
// carries ~500 domain codes whose messages are specific and worth showing verbatim,
// while the framework set is small and fixed.
const FRAMEWORK_ERROR_CODES = [
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'BAD_USER_INPUT',
  'BAD_REQUEST',
  'INTERNAL_SERVER_ERROR',
  'OPERATION_RESOLUTION_FAILURE',
  'PERSISTED_QUERY_NOT_FOUND',
  'PERSISTED_QUERY_NOT_SUPPORTED',
  'UNKNOWN_ERROR',
  'UNAUTHENTICATED',
  'NOT_AUTHORIZED',
  'FORBIDDEN',
];

export const getErrorMessage = (error) => {
  if (!error) {
    return DEFAULT_ERROR_MESSAGE;
  }

  const errorCodesToReturnMessage = [
    'INPUT_VALIDATION_ERROR',
    'PRX_SHIPMENT_ERROR',
    'FEDEX_API_ERROR',
    // POM-6242: the order acceptance rules write their own user-facing copy — it names the
    // drug and the state the order was blocked for, which a static translation cannot.
    'ORDER_REJECTED_BY_ACCEPTANCE_RULE',
    // POM-6238: core says why the void was refused (e.g. a reshipment already exists) — the
    // static translation hid the reason.
    'VOID_SHIPMENT_ERROR',
    // A duplicate catalog create names the drug and the strengths that already exist.
    'CATALOG_ALREADY_EXISTS',
    'INVALID_DRUG_CODE',
    'RX_ALREADY_SCANNED',
    'SCANNED_NDC_MISMATCH',
    'ASSIGNED_NDC_NOT_FOUND',
    'LOT_NDC_MISMATCH',
    'OVERRIDE_NDC_NOT_IN_INVENTORY',
    'OVERRIDE_NDC_PRODUCT_MISMATCH',
    'INVENTORY_NDC_NOT_FOUND_FOR_PRODUCT',
    'INVENTORY_NDC_INACTIVE_FOR_DEFAULT',
    'PARTNER_NDC_PREFERENCE_INVALID_NDC',
    'COA_LOT_NOT_IN_INVENTORY',
  ];

  if (errorCodesToReturnMessage.includes(error.code) && error.message) {
    return error.message;
  }

  if (error.i18nCode) {
    if (ERROR_TRANSLATIONS[error.i18nCode]) {
      return ERROR_TRANSLATIONS[error.i18nCode];
    }
    return DEFAULT_ERROR_MESSAGE;
  }

  return DEFAULT_ERROR_MESSAGE;
};

// Extracts a user-facing message from a caught GraphQL client error (graphql-request
// ClientError). Use for inline, form-level errors where the dialog stays open so the user
// can correct the input and resubmit.
//
// For domain errors it prefers the exact backend message — that is the most specific thing
// available (e.g. it names the offending NDC or drug code) — then a mapped i18n translation,
// then a generic default.
//
// Framework-level codes are the exception: their messages are developer-facing and must be
// translated or genericised instead of shown. Without this gate a stale stitched schema in
// the gatekeeper surfaced graphql-js's `Cannot query field "listProductNdcs" on type "Query".
// Did you mean ...` — a dump of sibling Query fields — straight into the Edit Default NDC
// dialog.
export const getGraphQLErrorMessage = (error) => {
  const firstError = error?.response?.errors?.[0];
  const extensions = firstError?.extensions || error?.response?.extensions || {};
  const { i18nCode, code } = extensions;

  const isFrameworkError = FRAMEWORK_ERROR_CODES.includes(code);

  if (firstError?.message && !isFrameworkError) {
    return firstError.message;
  }
  if (error?.response?.message && !isFrameworkError) {
    return error.response.message;
  }
  if (i18nCode && ERROR_TRANSLATIONS[i18nCode]) {
    return ERROR_TRANSLATIONS[i18nCode];
  }
  if (isFrameworkError) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return error?.message || DEFAULT_ERROR_MESSAGE;
};

export default getErrorMessage;
